import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { SEO, faqLd } from '../components/SEO';
import { StrategySessionModal } from '../components/StrategySessionModal';

const FAQS = [
  {
    q: 'What does a strategic event concierge do?',
    a: 'A strategic event concierge works on the client side to guide budget strategy, vendor alignment, contract and decision review, and oversight before and during a high-value event. The role is advisory — protecting the client\'s decisions and investment — rather than executing the production itself.',
  },
  {
    q: 'How is White Glove different from an event planner?',
    a: 'A planner typically manages design, logistics, and on-site execution. White Glove serves as an advisory layer focused on clarity, vendor alignment, contract guidance, and protecting the client\'s decision process — often working alongside a planner rather than replacing one.',
  },
  {
    q: 'When should I hire White Glove?',
    a: 'The ideal time is before contracts are signed, budgets are locked, and vendors are finalized. That is when strategic guidance can prevent the most expensive misalignment and ensure every commitment performs the way it should.',
  },
  {
    q: 'Does White Glove provide legal advice?',
    a: 'No. Contract review is provided strictly for event decision guidance and clarity, not legal advice. For binding interpretations, signing decisions, or disputes, we encourage consulting a licensed attorney.',
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const ourRole = [
  {
    title: 'Client-Side Representation',
    description: 'We act as your advocate throughout the planning process—ensuring your best interests are protected in every decision, conversation, and investment.',
  },
  {
    title: 'Strategic Oversight',
    description: 'We guide the direction of your event from a decision-making standpoint—keeping everything aligned with your priorities, budget, and expectations.',
  },
  {
    title: 'Vendor Alignment & Communication Guidance',
    description: "We help ensure communication across vendors remains clear, consistent, and aligned—without disrupting the planner's workflow.",
  },
  {
    title: 'Contract Review & Decision Support',
    description: "We walk through vendor agreements with you before you sign—highlighting key considerations and ensuring you fully understand what you're committing to.",
  },
];

const plannerFocuses = [
  'Event design and creative direction',
  'Logistics and production execution',
  'Timeline management',
  'Guest experience',
];

const packages = [
  {
    name: 'Concierge Advisory',
    packageKey: 'concierge_advisory',
    price: '$1,800',
    priceNote: 'Founders pricing · applied for early clients',
    description: 'Structured support for clients who want expert guidance without full planning.',
    features: [
      'Line-by-line budget architecture',
      'Vendor shortlist aligned to style and spend',
      'Contract and decision guidance',
      'Ongoing consultation as needed',
    ],
    idealFor: 'Clients who want expert direction and structure without handing over the planning entirely.',
    featured: false,
  },
  {
    name: 'Full Concierge Oversight',
    packageKey: 'full_concierge_oversight',
    price: '$3,500',
    priceNote: 'Founders pricing · applied for early clients',
    description: 'For clients who want consistent, high-level involvement throughout the process.',
    features: [
      'Vendor coordination and communication support',
      'Contract review and alignment guidance',
      'Budget monitoring and adjustments',
      'Continuous oversight from start to execution',
    ],
    idealFor: 'Busy professionals and clients who want the process handled with care, structure, and expertise.',
    featured: true,
  },
];

const eliteFeatures = [
  {
    title: 'Ongoing Strategic Oversight',
    description: 'Every decision is guided through a strategic lens—ensuring nothing is reactive, misaligned, or overlooked.',
  },
  {
    title: 'Client-Side Vendor Representation',
    description: 'We serve as your advocate throughout vendor engagement—ensuring every interaction, expectation, and decision supports your overall vision.',
  },
  {
    title: 'Contract Review & Guidance',
    description: 'Before signing, we walk through vendor agreements with you—highlighting key terms, potential risks, and areas that may require clarification.',
  },
  {
    title: 'Vendor Alignment & Communication Guidance',
    description: "We help maintain clarity and consistency across all vendor interactions—without interfering with your planner's execution.",
  },
  {
    title: 'Budget Oversight & Real-Time Adjustments',
    description: 'As proposals and invoices come in, we help you adjust and optimize your budget to stay aligned with your priorities.',
  },
  {
    title: 'Unlimited Advisory Access (Within Scope)',
    description: 'When decisions arise, you have direct access for guidance—so you never have to second-guess your next move.',
  },
  {
    title: 'Event Proximity Support',
    description: 'As your event approaches, we remain closely involved—ensuring everything is aligned and on track for execution.',
  },
];

const eliteSaves = [
  'Costly vendor misalignment',
  "Contracts you don't fully understand",
  'Overspending in the wrong areas',
  'Time lost navigating complex decisions',
  'Stress from uncertainty',
];

const eliteGains = [
  'Confidence in every decision',
  'A fully aligned vendor team',
  'Expert-level guidance without full planning overhead',
  'A smoother, more controlled experience',
];

const eliteForYouIf = [
  'You want expert eyes on every major decision',
  'You value clarity before committing',
  "You don't have time to manage every detail",
  'You want to get it right the first time',
];

const addOns = [
  {
    title: 'Vendor Sourcing',
    price: '$500–$1,000',
    description: 'Targeted vendor recommendations for clients who need trusted direction without a full package.',
  },
  {
    title: 'Budget Audit & Refinement',
    price: '$400–$750',
    description: 'A detailed review of your current budget and plan to identify gaps, inefficiencies, and missed opportunities.',
  },
  {
    title: 'Concierge Event Presence',
    price: '$1,000–$2,500',
    description: 'On-site support for alignment, flow, and elevated client experience without full planning or coordination.',
  },
  {
    title: 'Priority Access Upgrade',
    price: '$750–$1,500',
    description: 'Enhanced communication and support for clients who require a higher-touch response cadence.',
  },
  {
    title: 'Curated Rentals',
    price: 'Custom Pricing',
    description: 'Select offerings including draping, specialty décor, and inventory pieces for events requiring an added design layer.',
  },
];

const howItWorks = [
  {
    step: '01',
    title: 'Begin with strategy',
    description: 'We start with a conversation that brings clarity to your goals, priorities, budget, and immediate needs.',
  },
  {
    step: '02',
    title: 'Receive a tailored recommendation',
    description: 'Based on your event and level of support needed, we recommend the most appropriate service path.',
  },
  {
    step: '03',
    title: 'Move forward with confidence',
    description: 'From vendor guidance to ongoing concierge support, every step is designed to feel more organized, intentional, and refined.',
  },
];

const idealClientPoints = [
  'You want guidance without unnecessary overwhelm',
  'You value trusted recommendations and strong communication',
  'You want to avoid expensive mistakes',
  'You appreciate a polished, strategic approach',
];

export const Consulting = () => {
  const [modalState, setModalState] = useState({ open: false, packageKey: 'strategy_session', packageLabel: 'Strategy Session Only' });
  const openStrategyModal = (packageKey, packageLabel) =>
    setModalState({ open: true, packageKey, packageLabel });
  const closeStrategyModal = () => setModalState((s) => ({ ...s, open: false }));

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="consulting-page">
      <StrategySessionModal
        open={modalState.open}
        onClose={closeStrategyModal}
        packageKey={modalState.packageKey}
        packageLabel={modalState.packageLabel}
      />
      <SEO
        title="Event Concierge Services & Pricing | White Glove"
        description="Expert event advisory from $300: strategy sessions, vendor alignment, and full oversight for weddings and corporate events in Nashville."
        jsonLd={faqLd(FAQS)}
      />
      {/* HERO */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div {...fadeInUp}>
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">Nashville Event Advisory</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mt-4 mb-8">
              Nashville&apos;s strategic event concierge for a{' '}
              <span className="italic text-[#D4AF37]">more refined process</span>
            </h1>
            <p className="text-white/70 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-10">
              White Glove Event Concierge is designed for clients who want expert direction, trusted vendor collaboration, and a more elevated path forward. We step in as your strategic partner—helping you make smart decisions, avoid costly missteps, and move through the process with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => openStrategyModal('strategy_session', 'Strategy Session Only')}
                className="btn-primary"
                data-testid="consulting-hero-cta"
              >
                Book a Strategy Session
              </button>
              <a href="#packages" className="btn-secondary" data-testid="consulting-hero-explore">
                Explore Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO WITH IMAGE */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1769812343353-ae6ca1250bc2?q=80&w=1200&auto=format&fit=crop"
                alt="Elegant Event Setting"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
                Support that brings order to the process
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-10">
                Not every client needs a full-service planner. Sometimes what is needed most is an experienced professional who can step in, clarify the path, align the right people, and keep the process moving in the right direction. White Glove Event Concierge offers exactly that: high-touch, strategic support for clients who value professionalism, efficiency, and elevated execution.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-[#D4AF37] hover:gap-4 transition-all text-sm uppercase tracking-widest">
                Inquire Now <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW WE WORK ALONGSIDE YOUR PLANNER & VENDORS */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              How We Work Alongside Your Planner & Vendors
            </h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-3xl mx-auto">
              White Glove Event Concierge is not a replacement for your planner—we are a strategic layer of support that ensures every decision behind your event is aligned from the start.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="mb-16 max-w-3xl mx-auto text-center">
            <p className="text-white/70 text-lg leading-relaxed mb-4">
              We work alongside your planner and vendor team to bring clarity, structure, and oversight to the process—so each partner can focus on what they do best.
            </p>
            <p className="text-white text-lg leading-relaxed">
              Your planner leads design, logistics, and execution.
            </p>
            <p className="text-[#D4AF37] text-lg italic font-heading mt-2">
              We ensure the decisions driving those elements are informed, intentional, and aligned with your goals.
            </p>
          </motion.div>

          {/* Our Role */}
          <motion.div {...fadeInUp} className="mb-16">
            <h3 className="font-heading text-2xl md:text-3xl text-[#D4AF37] text-center mb-10">Our Role</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {ourRole.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-[#0a0a0a] border border-white/10 p-6"
                >
                  <h4 className="font-heading text-lg text-white mb-3">{item.title}</h4>
                  <p className="text-white/70 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Your Planner's Role */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-[#0a0a0a] border border-white/10 p-10 max-w-2xl mx-auto">
              <h3 className="font-heading text-2xl text-[#D4AF37] text-center mb-6">Your Planner&apos;s Role</h3>
              <p className="text-white/70 text-center mb-6">Your planner focuses on:</p>
              <ul className="space-y-3">
                {plannerFocuses.map((item) => (
                  <li key={item} className="flex items-center gap-3 justify-center">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* The Result */}
          <motion.div {...fadeInUp}>
            <div className="bg-[#D4AF37] p-10 text-center">
              <h3 className="font-heading text-2xl text-black mb-4">The Result</h3>
              <p className="text-black/80 text-lg leading-relaxed max-w-2xl mx-auto">
                Instead of overlapping roles or miscommunication, you gain a fully aligned team—each expert operating in their zone of excellence. The result is a more efficient process, stronger decisions, and a smoother event experience from start to finish.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STRATEGY SESSION */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <div className="bg-[#050505] p-10 md:p-16 border border-white/10">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">Start Here</span>
                  <h3 className="font-heading text-3xl md:text-4xl text-white mt-4 mb-2">
                    <span className="text-white">White Glove</span> Strategy Session
                  </h3>
                  <p className="text-[#D4AF37] text-2xl font-heading mb-6">$300</p>
                  <p className="text-white/70 leading-relaxed mb-8">
                    A focused 60–90 minute session designed to bring immediate clarity to your event, budget, and next steps.
                  </p>
                  <ul className="space-y-3 mb-8">
                    {['Personalized budget framework', 'Vendor roadmap based on your needs', 'Strategic recommendations', 'Real-time Q&A and problem solving'].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <span className="text-white/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-white/60 text-sm italic mb-8">
                    Best For: Clients in the early stages, or those who need a reset and a clear direction.
                  </p>
                  <button
                    type="button"
                    onClick={() => openStrategyModal('strategy_session', 'Strategy Session Only')}
                    className="inline-block px-8 py-3 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C9A432] transition-colors"
                    data-testid="strategy-session-cta"
                  >
                    Start Here
                  </button>
                </div>
                <div>
                  <img
                    src="/assets/founder.png"
                    alt="Strategy Session"
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="py-24 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">Service Packages</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4">
              Concierge Packages
            </h2>
          </motion.div>

          {/* Founding Client Pricing — luxury urgency */}
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto mb-14">
            <div className="relative bg-gradient-to-br from-[#141006] to-[#0a0905] border border-[#D4AF37]/40 p-8 md:p-10 text-center">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.3em] font-semibold">
                Founding Client Pricing
              </div>
              <p className="text-white/85 text-base md:text-lg leading-relaxed font-heading italic mb-3">
                The rates below are reserved for our founding clients — a select group welcomed at the beginning of this chapter.
              </p>
              <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
                Once these limited founding positions are filled, standard pricing takes effect. If you&apos;ve been waiting for the right moment to secure expert guidance at this level, this is it.
              </p>
              <p className="mt-5 text-[#D4AF37] text-xs uppercase tracking-[0.25em]">
                Limited availability · Secure your position today
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative p-8 md:p-10 border transition-all ${
                  pkg.featured
                    ? 'bg-[#0a0a0a] border-[#D4AF37]'
                    : 'bg-[#0a0a0a] border-white/10 hover:border-white/30'
                }`}
                data-testid={`consulting-package-${index}`}
              >
                {pkg.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.2em] font-semibold">
                    Most Requested
                  </span>
                )}
                <h3 className="font-heading text-3xl text-white mb-2">{pkg.name}</h3>
                <div className="mb-6">
                  <p className="font-heading text-4xl text-[#D4AF37] leading-none">{pkg.price}</p>
                  {pkg.priceNote && (
                    <p className="text-white/50 text-xs italic mt-2">{pkg.priceNote}</p>
                  )}
                </div>
                <p className="text-white/70 leading-relaxed mb-8">{pkg.description}</p>

                <div className="border-t border-white/10 pt-6 mb-6">
                  <p className="text-white text-xs uppercase tracking-widest mb-4">Included:</p>
                  <ul className="space-y-3">
                    {pkg.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-white/50 text-xs italic mb-6">
                  Contract review is provided for guidance and clarity. White Glove Event Concierge does not provide legal advice.
                </p>

                <p className="text-white/60 text-sm italic mb-8">
                  Ideal For: {pkg.idealFor}
                </p>

                <button
                  type="button"
                  onClick={() => openStrategyModal(pkg.packageKey, pkg.name)}
                  className={`block w-full text-center py-3 text-sm uppercase tracking-widest transition-colors ${
                    pkg.featured
                      ? 'bg-[#D4AF37] text-black hover:bg-[#C9A432]'
                      : 'border border-white/20 text-white hover:bg-white/5'
                  }`}
                  data-testid={`consulting-package-cta-${index}`}
                >
                  Request Strategy Session
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ELITE CONCIERGE */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">Bespoke Experience</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mt-4 mb-4">
              Elite Concierge
            </h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-3xl mx-auto">
              A fully tailored experience designed for clients who require a higher level of involvement, discretion, and strategic oversight.
            </p>
            <p className="text-white/60 text-sm italic mt-4 mb-6 max-w-2xl mx-auto">
              This offering is customized based on scope, complexity, and level of support required.
            </p>
            <p className="text-[#D4AF37] text-lg uppercase tracking-widest">
              Investment: Custom, based on engagement.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              This is a high-touch advisory experience for clients who want expert guidance across every major decision.
            </p>
            <p className="text-white text-lg leading-relaxed">
              You are not navigating this process alone—you have a dedicated strategic partner ensuring every detail, vendor, and investment is aligned from start to finish.
            </p>
          </motion.div>

          {/* What's Included */}
          <motion.div {...fadeInUp} className="mb-16">
            <h3 className="font-heading text-2xl text-white text-center mb-10">What&apos;s Included</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {eliteFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-[#050505] border border-white/10 p-6"
                >
                  <h4 className="font-heading text-lg text-[#D4AF37] mb-2">{feature.title}</h4>
                  <p className="text-white/70 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* What This Saves You / What You Gain */}
          <motion.div {...fadeInUp} className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-[#050505] border border-white/10 p-8">
              <h3 className="font-heading text-xl text-white mb-6">What This Saves You</h3>
              <ul className="space-y-3">
                {eliteSaves.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-white/70">
                    <span className="text-red-400 mt-1">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#050505] border border-[#D4AF37]/30 p-8">
              <h3 className="font-heading text-xl text-white mb-6">What You Gain</h3>
              <ul className="space-y-3">
                {eliteGains.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* This Is For You If */}
          <motion.div {...fadeInUp} className="bg-[#D4AF37] p-10 mb-8">
            <h3 className="font-heading text-2xl text-black text-center mb-8">This Is For You If:</h3>
            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {eliteForYouIf.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-black flex-shrink-0" />
                  <span className="text-black">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="text-center">
            <p className="text-white/50 text-xs italic mb-6">
              Contract review is provided for guidance and clarity. White Glove Event Concierge does not provide legal advice.
            </p>
            <button
              type="button"
              onClick={() => openStrategyModal('elite_concierge', 'Elite Concierge (Custom)')}
              className="inline-block px-8 py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C9A432] transition-colors"
              data-testid="elite-cta"
            >
              Inquire for a Private Consultation
            </button>
          </motion.div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              Additional ways to support your event
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addOns.map((addon, index) => (
              <motion.div
                key={addon.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-[#0a0a0a] border border-white/10 p-8 hover:border-[#D4AF37]/40 transition-colors"
                data-testid={`addon-${index}`}
              >
                <h4 className="font-heading text-xl text-white mb-3">{addon.title}</h4>
                <p className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4">{addon.price}</p>
                <p className="text-white/70 text-sm leading-relaxed">{addon.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white">
              How it works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`how-it-works-${index}`}
              >
                <p className="font-heading text-6xl text-[#D4AF37]/40 mb-4">{step.step}</p>
                <h3 className="font-heading text-xl text-white mb-4">{step.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Who this is for
              </h2>
              <p className="text-white/70 text-lg leading-relaxed">
                White Glove Event Concierge is for clients who value expertise, professionalism, and a higher level of service. Whether you are planning a corporate gathering, social celebration, or custom event experience, our role is to help bring structure to the process and confidence to every decision.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ul className="space-y-4">
                {idealClientPoints.map((point) => (
                  <li key={point} className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-white text-lg">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AUTHORITY */}
      <section className="py-24 bg-[#1B1B1B]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
              Built on experience. Designed for ease.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed">
              With over 20 years of experience across event operations, venue leadership, client experience, and vendor collaboration, White Glove Event Concierge was created to bridge the gap between vision and execution—bringing a more strategic, elevated level of support to the event process.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#050505] border-t border-white/10" data-testid="consulting-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-14">
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Frequently Asked</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4">
              Common questions, <em className="italic text-[#D4AF37]">answered plainly</em>
            </h2>
          </motion.div>
          <dl className="space-y-4">
            {FAQS.map((item, i) => (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-[#0a0a0a] border border-white/10 hover:border-[#D4AF37]/30 transition-colors"
              >
                <details className="group" data-testid={`consulting-faq-${i}`}>
                  <summary className="flex items-start justify-between gap-4 cursor-pointer p-6 list-none">
                    <dt className="font-heading text-white text-lg md:text-xl pr-2">{item.q}</dt>
                    <span className="text-[#D4AF37] text-2xl leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
                  </summary>
                  <dd className="px-6 pb-6 text-white/70 text-base leading-relaxed">{item.a}</dd>
                </details>
              </motion.div>
            ))}
          </dl>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-[#050505] border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Start with clarity
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              The right support can change the entire experience. Begin with a White Glove Strategy Session and discover the most refined path forward for your event.
            </p>
            <button
              type="button"
              onClick={() => openStrategyModal('strategy_session', 'Strategy Session Only')}
              className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C9A432] transition-colors"
              data-testid="consulting-final-cta"
            >
              Book a Strategy Session <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
